<template>
  <div class="p-4 md:p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Blocchi automatici</h2>
        <p class="text-sm text-gray-500 mt-0.5">
          Azioni eseguite dal sistema senza intervento umano
        </p>
      </div>
      <button
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-white text-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800 text-sm transition"
        :disabled="loading"
        @click="refresh"
      >
        <span class="material-icons text-sm" :class="{ 'animate-spin': loading }">refresh</span>
        Aggiorna
      </button>
    </div>

    <!-- Filtri -->
    <div class="flex flex-wrap gap-3 mb-5">
      <div class="flex items-center gap-2">
        <span class="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Periodo:</span>
        <select
          v-model="filters.since_hours"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="refresh"
        >
          <option :value="24">Ultime 24h</option>
          <option :value="168">Ultima settimana</option>
          <option :value="720">Ultimo mese</option>
          <option :value="2160">Ultimi 3 mesi</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">Tipo:</span>
        <select
          v-model="filters.action"
          class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          @change="refresh"
        >
          <option value="">Tutti</option>
          <option value="AUTO_BLOCK_HIGH_UNPAID">Auto-blocco: alto non pagato</option>
          <option value="AUTO_BLOCK_EMAIL_TYPO">Auto-blocco: email sospetta</option>
          <option value="AUTO_BLOCK_SHARED_CARD">Auto-blocco: carta condivisa</option>
          <option value="AUTO_WHITELIST_CARD">Auto-whitelist carta</option>
        </select>
      </div>
    </div>

    <!-- Riepilogo per tipo (calcolato dal client) -->
    <div v-if="!loading && actions.length > 0" class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      <div
        v-for="(count, type) in actionSummary"
        :key="type"
        class="bg-white dark:bg-gray-900 rounded-xl shadow px-4 py-3 text-center"
      >
        <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ count }}</p>
        <p class="text-xs text-gray-500 mt-0.5 truncate" :title="type">
          {{ actionLabel(type) }}
        </p>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-2">
      <div
        v-for="i in 8"
        :key="i"
        class="h-12 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!actions.length"
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">smart_toy</span>
      <p class="text-lg font-medium">Nessuna azione automatica nel periodo selezionato.</p>
    </div>

    <!-- Tabella -->
    <div v-else class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500 uppercase">
            <th class="px-4 py-2 text-left">Tipo azione</th>
            <th class="px-4 py-2 text-left hidden sm:table-cell">Utente target</th>
            <th class="px-4 py-2 text-left hidden md:table-cell">Carta target</th>
            <th class="px-4 py-2 text-left">Motivazione</th>
            <th class="px-4 py-2 text-left hidden lg:table-cell">Data</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          <tr
            v-for="act in actions"
            :key="act._id"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition"
          >
            <!-- Tipo azione badge -->
            <td class="px-4 py-3">
              <span
                class="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                :class="actionBadgeClass(act.action)"
              >
                {{ actionLabel(act.action) }}
              </span>
            </td>

            <!-- Target uid -->
            <td class="px-4 py-3 hidden sm:table-cell">
              <router-link
                v-if="act.target_uid"
                :to="{ name: 'menu.antifrode.utenti.detail', params: { uid: act.target_uid } }"
                class="text-blue-600 hover:underline text-xs font-mono"
              >
                #{{ act.target_uid }}
              </router-link>
              <span v-else class="text-gray-300 text-xs">—</span>
            </td>

            <!-- Target card -->
            <td class="px-4 py-3 text-xs text-gray-400 font-mono hidden md:table-cell">
              {{ act.target_card_id ? `#${act.target_card_id}` : '—' }}
            </td>

            <!-- Motivazione -->
            <td class="px-4 py-3 text-gray-500 text-xs max-w-[220px]">
              <p class="truncate" :title="act.reason">{{ act.reason || '—' }}</p>
              <!-- metadata sintetico se presente -->
              <p v-if="parsedMeta(act.metadata)" class="text-gray-300 text-xs truncate mt-0.5" :title="act.metadata">
                {{ parsedMeta(act.metadata) }}
              </p>
            </td>

            <!-- Data -->
            <td class="px-4 py-3 text-gray-400 text-xs whitespace-nowrap hidden lg:table-cell">
              {{ relativeDate(act.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <div class="px-4 py-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400">
        {{ actions.length }} azioni
        <span v-if="actions.length >= 100"> · mostra max 100, restringi il periodo o il tipo</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      filters: {
        since_hours: 168,
        action: '',
      },
    };
  },
  computed: {
    ...mapGetters(['antifrode_autoActions', 'antifrode_loading']),
    actions() {
      return this.antifrode_autoActions || [];
    },
    loading() {
      return this.antifrode_loading;
    },
    actionSummary() {
      const map = {};
      for (const act of this.actions) {
        map[act.action] = (map[act.action] || 0) + 1;
      }
      return map;
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch('fetchAntifrodeAutoActions', {
        since_hours: this.filters.since_hours,
        action: this.filters.action,
        limit: 100,
      });
    },
    // ── helpers UI ────────────────────────────────────────────────
    actionLabel(type) {
      const labels = {
        AUTO_BLOCK_HIGH_UNPAID: 'Blocco: alto debito',
        AUTO_BLOCK_EMAIL_TYPO: 'Blocco: email sospetta',
        AUTO_BLOCK_SHARED_CARD: 'Blocco: carta condivisa',
        AUTO_WHITELIST_CARD: 'Whitelist carta',
        BLOCK_USER: 'Blocco manuale',
        UNBLOCK_USER: 'Sblocco manuale',
        WHITELIST_CARD: 'Whitelist manuale',
        ADD_NOTE: 'Nota aggiunta',
      };
      return labels[type] || type.replace(/_/g, ' ');
    },
    actionBadgeClass(type) {
      if (type?.startsWith('AUTO_BLOCK') || type === 'BLOCK_USER') {
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      }
      if (type?.includes('WHITELIST') || type === 'UNBLOCK_USER') {
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
      }
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    },
    parsedMeta(raw) {
      if (!raw) return null;
      try {
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const keys = Object.keys(obj);
        if (!keys.length) return null;
        return keys.slice(0, 2).map((k) => `${k}: ${obj[k]}`).join(' · ');
      } catch {
        return null;
      }
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const mins = Math.floor(diffMs / 60000);
      const hours = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 2) return 'adesso';
      if (mins < 60) return `${mins} min fa`;
      if (hours < 24) return `${hours}h fa`;
      if (days < 30) return `${days} gg fa`;
      return `${Math.floor(days / 30)} mesi fa`;
    },
  },
};
</script>

<style></style>
